import { useState } from "react"

import type { Column, Files } from "../types"
import type { ColumnDef } from "@tanstack/react-table"

import {
  AlertTriangle,
  Calendar,
  Check,
  Hash,
  List,
  Minus,
  MoreVertical,
  Pencil,
  RefreshCw,
  ToggleLeft,
  Trash2,
  Type,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { renderFileIcon } from "@/features/chat/components/file-picker/utils"
import { cn } from "@/lib/utils"
import { useParserSelectionStore } from "@/stores/parser-selection-store"

import { EditColumnDialog } from "./edit-column-dialog"
import { ColumnCell, ColumnHeader, HighlightedText } from "./table-columns-components"
import { useDeleteColumnMutation } from "../hooks/use-delete-columns-mutation"
import { useDeleteDocumentMutation } from "../hooks/use-delete-document-mutation"

export type ColumnDataType = "string" | "number" | "boolean" | "options"

function getColumnTypeIcon(dataType: string) {
  switch (dataType) {
    case "number":
      return <Hash className="text-muted-foreground size-3.5" />
    case "boolean":
      return <ToggleLeft className="text-muted-foreground size-3.5" />
    case "date":
      return <Calendar className="text-muted-foreground size-3.5" />
    case "options":
      return <List className="text-muted-foreground size-3.5" />
    default:
      return <Type className="text-muted-foreground size-3.5" />
  }
}

export type TableRow = {
  documentName: string | null
  wasTruncated?: boolean
  [columnId: string]: ColumnDataType | null | string | number | boolean | undefined
}

type BaseColumnOptions = {
  t?: (key: string) => string
  isRunParserPending: boolean
  currentSessionId: string | null
  searchTerm?: string
}

export function createBaseColumns(options: BaseColumnOptions): ColumnDef<TableRow>[] {
  const { t, isRunParserPending, currentSessionId, searchTerm = "" } = options

  return [
    {
      id: "documentName",
      header: () => (
        <ColumnHeader>{t?.("pages.ai_parser.table.document") || "Document"}</ColumnHeader>
      ),
      cell: ({ row }) => {
        const deleteDocumentMutation = useDeleteDocumentMutation()
        const selectMode = useParserSelectionStore((state) => state.selectMode)
        const selectedFileIndices = useParserSelectionStore((state) => state.selectedFileIndices)
        const toggleFileSelection = useParserSelectionStore((state) => state.toggleFileSelection)
        const selectFileRange = useParserSelectionStore((state) => state.selectFileRange)
        const lastSelectedFileIndex = useParserSelectionStore(
          (state) => state.lastSelectedFileIndex
        )
        const enterSelectModeWithFile = useParserSelectionStore(
          (state) => state.enterSelectModeWithFile
        )

        function deleteDocument() {
          if (!currentSessionId || !row.original.documentName) {
            return
          }

          deleteDocumentMutation.mutate({
            sessionId: currentSessionId,
            filename: row.original.documentName,
          })
        }

        if (!row.original.documentName) {
          return <div className="h-10 w-full" />
        }

        const rowNumber = row.index + 1
        const rowIndex = row.index
        const isSelected = selectedFileIndices.has(rowIndex)

        return (
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {selectMode ? (
              <button
                aria-label="Toggle row selection"
                type="button"
                className={cn(
                  "focus-visible:ring-ring relative flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  isSelected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-muted-foreground/40 text-muted-foreground"
                )}
                onClick={(event) => {
                  event.stopPropagation()
                  if (event.shiftKey && lastSelectedFileIndex !== null) {
                    selectFileRange(lastSelectedFileIndex, rowIndex)
                  } else {
                    toggleFileSelection(rowIndex)
                  }
                }}
              >
                {rowNumber}
              </button>
            ) : (
              <button
                aria-label="Select row"
                className="group focus-visible:ring-ring relative flex size-6 shrink-0 items-center justify-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  if (event.shiftKey && lastSelectedFileIndex !== null) {
                    selectFileRange(lastSelectedFileIndex, rowIndex)
                  } else {
                    enterSelectModeWithFile(rowIndex)
                  }
                }}
              >
                <span className="text-muted-foreground text-sm transition-opacity group-hover:opacity-0">
                  {rowNumber}
                </span>
                <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="border-muted-foreground/40 text-muted-foreground flex size-6 items-center justify-center rounded-full border text-xs font-medium">
                    {rowNumber}
                  </span>
                </span>
              </button>
            )}
            <ColumnCell className="group/cell flex max-w-none min-w-0 flex-1 items-center justify-between gap-1.5">
              <span className="shrink-0 [&>svg]:mr-0 [&>svg]:size-4">
                {renderFileIcon(row.original.documentName)}
              </span>
              <span
                className="flex min-w-0 flex-1 shrink items-center overflow-hidden text-ellipsis whitespace-nowrap"
                title={row.original.documentName}
              >
                <HighlightedText searchTerm={searchTerm} text={row.original.documentName} />
              </span>
              {row.original.wasTruncated && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle className="text-warning-ink size-4 shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      {t?.("pages.ai_parser.table.file_truncated") ||
                        "This file was too large and some content was not processed"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
              {row.original.documentName && (
                <Button
                  disabled={deleteDocumentMutation.isPending || isRunParserPending}
                  variant="ghost"
                  className={cn(
                    "text-muted-foreground ml-2 !size-7 shrink-0 rounded-full !p-0 transition-opacity",
                    deleteDocumentMutation.isPending
                      ? "opacity-100"
                      : "opacity-0 group-hover/cell:opacity-100"
                  )}
                  onClick={deleteDocument}
                >
                  {deleteDocumentMutation.isPending ? <Spinner /> : <X className="size-4" />}
                </Button>
              )}
            </ColumnCell>
          </div>
        )
      },
      minSize: 200,
      size: 280,
      maxSize: 500,
      enableResizing: true,
    },
  ]
}

type CustomColumnOptions = {
  columns: Column[]
  files: Files
  t?: (key: string) => string
  isRunParserPending: boolean
  searchTerm?: string
}

export function createCustomColumns(options: CustomColumnOptions): ColumnDef<TableRow>[] {
  const { columns, files, t, isRunParserPending, searchTerm = "" } = options

  return columns.map((column) => ({
    id: column.id,
    minSize: 180,
    size: 250,
    maxSize: 600,
    enableResizing: true,
    header: () => {
      const deleteColumnMutation = useDeleteColumnMutation()
      const [isMenuOpen, setIsMenuOpen] = useState(false)

      const columnNeedsReRun = files.some((file) => {
        const extractedValues = file.extracted_values
        if (!extractedValues) return false
        const hasBeenExtractedBefore = column.id in extractedValues
        return hasBeenExtractedBefore && column.requires_extraction
      })

      return (
        <ColumnHeader className="group w-full min-w-0 justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0">{getColumnTypeIcon(column.data_type)}</span>
                  <span
                    className="font-display min-w-0 overflow-hidden font-bold text-ellipsis whitespace-nowrap capitalize"
                    title={column.name}
                  >
                    {column.name}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-medium">{column.name}</p>
                {column.description && (
                  <p className="text-muted-foreground mt-1 text-xs">{column.description}</p>
                )}
              </TooltipContent>
            </Tooltip>
            {columnNeedsReRun && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-warning/30 text-warning-ink flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium">
                    <RefreshCw className="size-3" />
                    <span className="hidden sm:inline">
                      {t?.("pages.ai_parser.table.re_run_short") || "Re-run"}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {t?.("pages.ai_parser.table.re_run_required") ||
                      "Column was modified - re-run to update values"}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "size-6 shrink-0 p-0 transition-opacity",
                  isMenuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">
                  {t?.("pages.ai_parser.table.column_options") || "Column options"}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <EditColumnDialog column={column}>
                <DropdownMenuItem
                  disabled={isRunParserPending}
                  onSelect={(e) => e.preventDefault()}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t?.("pages.ai_parser.table.edit_column") || "Edit column"}
                </DropdownMenuItem>
              </EditColumnDialog>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                disabled={isRunParserPending || deleteColumnMutation.isPending}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  deleteColumnMutation.mutate(column.id)
                }}
                onSelect={(e) => {
                  e.preventDefault()
                }}
              >
                {deleteColumnMutation.isPending ? <Spinner /> : <Trash2 className="mr-2 h-4 w-4" />}
                {t?.("pages.ai_parser.table.delete_column") || "Delete column"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ColumnHeader>
      )
    },
    cell: ({ row }) => {
      if (row.original[column.id] === undefined) {
        return
      }

      if (row.original[column.id] === null) {
        return (
          <ColumnCell className="bg-muted/50">
            <p className="font-display text-muted-foreground/70 flex items-center gap-1.5 text-sm italic">
              <Minus className="size-3" />
              {t?.("pages.ai_parser.table.na_value") || "N/A"}
            </p>
          </ColumnCell>
        )
      }

      if (typeof row.original[column.id] === "boolean") {
        const isTrue = row.original[column.id] as boolean

        return (
          <ColumnCell
            className={cn(
              "font-medium",
              isTrue ? STATUS_TONES.success.soft : STATUS_TONES.destructive.soft
            )}
          >
            <span className="flex items-center gap-1.5">
              {isTrue ? <Check className="size-4" /> : <X className="size-4" />}
              {isTrue
                ? t?.("pages.ai_parser.table.yes") || "Yes"
                : t?.("pages.ai_parser.table.no") || "No"}
            </span>
          </ColumnCell>
        )
      }

      if (column.data_type === "options" && column.options) {
        const selectedValue = row.original[column.id] as string
        const selectedOption = column.options.find((opt) => opt.label === selectedValue)

        if (selectedOption) {
          return (
            <ColumnCell style={{ backgroundColor: `${selectedOption.color}15` }}>
              <span
                className="font-display text-sm font-medium"
                style={{ color: selectedOption.color }}
              >
                {selectedOption.label}
              </span>
            </ColumnCell>
          )
        }
      }

      const filename = row.original.documentName ?? undefined
      const cellValue = row.original[column.id]?.toString() ?? ""

      return (
        <ColumnCell isPreviewable columnId={column.id} filename={filename} text={cellValue}>
          <p className={cn("font-display overflow-hidden text-sm text-ellipsis whitespace-nowrap")}>
            <HighlightedText searchTerm={searchTerm} text={cellValue} />
          </p>
        </ColumnCell>
      )
    },
  }))
}
